import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Separator } from './ui/separator';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Instagram, Twitter, Linkedin, Youtube } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  return (
    <footer className="w-full border-t border-border bg-[hsl(var(--surface-1))] mt-20">
      <div className="container max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        {/* Newsletter Section */}
        <div className="max-w-md mx-auto text-center mb-12">
          <h3 className="font-display text-2xl font-bold mb-3">Stay in the Loop</h3>
          <p className="text-muted-foreground text-sm mb-5">
            Get the latest from B&B Magazine delivered to your inbox.
          </p>
          <form className="flex gap-2" onSubmit={async (e) => {
            e.preventDefault();
            if (!email) {
              toast.error('Please enter your email');
              return;
            }
            
            try {
              setLoading(true);
              await axios.post(`${API_URL}/api/newsletter/subscribe`, { email });
              toast.success('Successfully subscribed to newsletter!');
              setEmail('');
            } catch (error) {
              console.error('Newsletter subscription error:', error);
              if (error.response?.data?.detail) {
                toast.error(error.response.data.detail);
              } else {
                toast.error('Failed to subscribe. Please try again.');
              }
            } finally {
              setLoading(false);
            }
          }}>
            <Input
              type="email"
              placeholder="Your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="newsletter-input"
              className="flex-1 bg-background"
              required
              disabled={loading}
            />
            <Button 
              type="submit" 
              data-testid="newsletter-submit-button"
              className="font-ui"
              disabled={loading}
            >
              {loading ? 'Subscribing...' : 'Subscribe'}
            </Button>
          </form>
        </div>

        <Separator className="mb-8" />

        {/* Footer Links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
          <div>
            <h4 className="font-ui font-semibold text-sm mb-3">Content</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/features" className="text-muted-foreground hover:text-primary transition-colors duration-200">
                  Features
                </Link>
              </li>
              <li>
                <Link to="/interviews" className="text-muted-foreground hover:text-primary transition-colors duration-200">
                  Interviews
                </Link>
              </li>
              <li>
                <Link to="/videos" className="text-muted-foreground hover:text-primary transition-colors duration-200">
                  Videos
                </Link>
              </li>
              <li>
                <Link to="/shorts" className="text-muted-foreground hover:text-primary transition-colors duration-200">
                  Shorts
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-ui font-semibold text-sm mb-3">Focus</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/brands" className="text-muted-foreground hover:text-primary transition-colors duration-200">
                  Brands
                </Link>
              </li>
              <li>
                <Link to="/creators" className="text-muted-foreground hover:text-primary transition-colors duration-200">
                  Creators
                </Link>
              </li>
              <li>
                <Link to="/covers" className="text-muted-foreground hover:text-primary transition-colors duration-200">
                  Magazine Covers
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-ui font-semibold text-sm mb-3">About</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/about" className="text-muted-foreground hover:text-primary transition-colors duration-200">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-muted-foreground hover:text-primary transition-colors duration-200">
                  Contact
                </Link>
              </li>
              <li>
                <Link to="/advertise" className="text-muted-foreground hover:text-primary transition-colors duration-200">
                  Advertise
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="font-ui font-semibold text-sm mb-3">Follow Us</h4>
            <div className="flex gap-3">
              <a 
                href="https://instagram.com/thebnbmag" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors duration-200"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a 
                href="https://youtube.com/@thebnbmag" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary transition-colors duration-200"
                aria-label="YouTube"
              >
                <Youtube className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>

        {/* Copyright */}
        <div className="text-center text-sm text-muted-foreground">
          <p>&copy; {currentYear} B&B Magazine. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
};